import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../hooks/useLanguage';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card } from '../components/ui/card';
import {
  Monitor, ArrowLeft, ArrowRight, Search, Plus, Minus, Trash2, ShoppingCart,
  CreditCard, Banknote, Building2, Percent, DollarSign, Clock, CheckCircle2,
  Printer, RotateCcw, X, ChefHat, UtensilsCrossed, Truck, Package,
  Sun, Moon, History, Hand, Flame, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import QRCode from 'react-qr-code';

// ===== SHIFT GATE =====
function ShiftGate({ shift, onOpenShift, t, loading, onBackToDashboard }) {
  const [openingCash, setOpeningCash] = useState('');
  const handleOpen = () => {
    onOpenShift(parseFloat(openingCash) || 0);
  };
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="pos-shift-gate">
      <Card className="w-full max-w-md p-8 text-center space-y-6">
        <div className="w-20 h-20 mx-auto rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg shadow-amber-500/20">
          <Monitor className="w-10 h-10 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-foreground">{t('pos.title')}</h1>
          <p className="text-muted-foreground mt-1">{t('pos.startShift')}</p>
        </div>
        <div className="space-y-3 text-start">
          <label className="text-sm font-medium text-foreground">{t('pos.openingCash')} ({t('common.sar')})</label>
          <Input
            type="number" value={openingCash} onChange={e => setOpeningCash(e.target.value)}
            placeholder="0.00" className="text-center text-xl h-14" data-testid="pos-opening-cash"
            onKeyDown={e => e.key === 'Enter' && handleOpen()}
          />
        </div>
        <Button onClick={handleOpen} disabled={loading} className="w-full h-12 text-base bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700" data-testid="pos-open-shift-btn">
          {t('pos.openShift')}
        </Button>
        <Button variant="ghost" onClick={onBackToDashboard} className="w-full text-muted-foreground hover:text-foreground gap-2" data-testid="pos-back-to-dashboard-btn">
          <ArrowRight className="h-4 w-4" />
          {t('pos.backToDashboard')}
        </Button>
      </Card>
    </div>
  );
}

// ===== PRINT HELPERS (self-contained, no external resources) =====
const buildPrintDoc = (bodyHtml, paperWidth, extraCss = '') => {
  const w = paperWidth === 58 ? 58 : 80;
  const contentW = w === 58 ? 48 : 72;
  const pad = w === 58 ? '2mm 1mm' : '3mm 1.5mm';
  return `<!DOCTYPE html><html dir="rtl" lang="ar"><head><meta charset="utf-8">
    <style>
      @page { size: ${w}mm auto; margin: 0; }
      html, body { margin: 0 !important; padding: 0 !important; background: #fff !important; width: ${w}mm; }
      * { box-sizing: border-box; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
      body { font-family: 'IBM Plex Sans Arabic','Tajawal','Segoe UI',Tahoma,Arial,sans-serif; color: #000; }
      table { border-collapse: collapse; width: 100%; }
      .print-receipt {
        width: ${contentW}mm !important; max-width: ${contentW}mm !important;
        margin: 0 auto !important; padding: ${pad} !important; color: #000;
      }
      ${extraCss}
    </style></head><body>${bodyHtml}</body></html>`;
};

const printHtmlInFrame = (printDoc) => {
  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;';
  let done = false;
  const trigger = () => {
    if (done) return;
    const idoc = iframe.contentWindow && iframe.contentWindow.document;
    if (!idoc || !idoc.querySelector('.print-receipt')) return;
    done = true;
    try {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    } catch (e) {
      window.print();
    }
    setTimeout(() => { try { document.body.removeChild(iframe); } catch (e) {} }, 1500);
  };
  iframe.onload = () => setTimeout(trigger, 300);
  document.body.appendChild(iframe);
  const doc = iframe.contentWindow.document;
  doc.open();
  doc.write(printDoc);
  doc.close();
  setTimeout(trigger, 800);
  setTimeout(trigger, 1600);
};

// ===== RECEIPT MODAL =====
function ReceiptModal({ order, branch, businessSettings, onClose, onNewOrder, t, dir }) {
  const receiptRef = useRef(null);
  const [paperWidth, setPaperWidth] = useState(branch?.receipt_paper_width || 80);
  const [autoKitchen, setAutoKitchen] = useState(() => localStorage.getItem('zouqai_pos_auto_kitchen') !== 'false');
  const toggleAutoKitchen = () => { const n = !autoKitchen; setAutoKitchen(n); localStorage.setItem('zouqai_pos_auto_kitchen', String(n)); };

  // Resolve business identity for the receipt header
  const businessNameAr = businessSettings?.business_name_ar || '';
  const businessNameEn = businessSettings?.business_name_en || '';
  const vatNumber = businessSettings?.vat_number || '';
  const businessAddress = businessSettings?.address || '';
  const businessCity = businessSettings?.city || '';
  const logoUrl = businessSettings?.logo_url || '';

  const handlePrint = () => {
    const w = paperWidth === 58 ? 58 : 80;
    const f = w === 58 ? 11 : 13;
    const storeFont = w === 58 ? 14 : 17;
    const totalFont = w === 58 ? 13 : 16;
    const orderDate2 = new Date(order.created_at);
    const fmt = (v) => Number(v || 0).toFixed(2);
    const sar = t('common.sar');
    const qrSvg = receiptRef.current?.querySelector('.receipt-qr svg')?.outerHTML || '';
    const showLogo = logoUrl && (logoUrl.startsWith('/') || logoUrl.startsWith('data:') || logoUrl.startsWith(window.location.origin));
    const row = (label, value) => `<div style="display:flex;justify-content:space-between;margin:2px 0;"><span>${label}</span><span>${value}</span></div>`;
    const itemsRows = (order.items || []).map(it => `
      <tr>
        <td style="text-align:right;padding:3px 0;border-bottom:1px dotted #999;">${it.name_ar || it.name}</td>
        <td style="text-align:center;padding:3px 0;border-bottom:1px dotted #999;">${it.quantity}</td>
        <td style="text-align:center;padding:3px 0;border-bottom:1px dotted #999;">${it.price != null ? fmt(it.price) : '-'}</td>
        <td style="text-align:left;padding:3px 0;border-bottom:1px dotted #999;font-weight:600;">${fmt(it.line_total)}</td>
      </tr>`).join('');
    const body = `
    <div class="print-receipt" dir="rtl" style="font-size:${f}px;line-height:1.55;">
      <div style="text-align:center;border-bottom:2px dashed #000;padding-bottom:8px;margin-bottom:8px;">
        ${showLogo ? `<img src="${logoUrl}" style="width:52px;height:52px;object-fit:contain;margin:0 auto 4px;display:block;" />` : ''}
        ${businessNameAr ? `<p style="margin:0;font-size:${storeFont}px;font-weight:700;">${businessNameAr}</p>` : ''}
        ${businessNameEn && businessNameEn !== businessNameAr ? `<p style="margin:2px 0 0;font-size:${f - 2}px;">${businessNameEn}</p>` : ''}
        ${branch?.name ? `<p style="margin:2px 0 0;font-size:${businessNameAr ? f - 2 : storeFont}px;${businessNameAr ? '' : 'font-weight:700;'}">${businessNameAr ? t('pos.branch') + ': ' + branch.name : branch.name}</p>` : ''}
        ${businessAddress ? `<p style="margin:2px 0 0;font-size:${f - 3}px;">${businessAddress}${businessCity ? ' - ' + businessCity : ''}</p>` : (branch?.address ? `<p style="margin:2px 0 0;font-size:${f - 3}px;">${branch.address}</p>` : '')}
        ${branch?.phone ? `<p style="margin:1px 0 0;font-size:${f - 3}px;">${branch.phone}</p>` : ''}
        ${vatNumber ? `<p style="margin:3px 0 0;font-size:${f - 3}px;">VAT: ${vatNumber}</p>` : ''}
        <p style="margin:5px auto 0;display:inline-block;border:1px solid #000;padding:1px 8px;font-weight:700;font-size:${f - 1}px;">${t('pos.simplifiedTaxInvoice')}</p>
      </div>
      ${row(t('pos.orderNumber'), '#' + order.order_number)}
      ${row(t('pos.cashier'), order.cashier_name || '')}
      <div style="display:flex;justify-content:space-between;border-bottom:1px dashed #000;padding-bottom:6px;margin-bottom:6px;">
        <span>${orderDate2.toLocaleDateString('ar-SA')}</span><span>${orderDate2.toLocaleTimeString('ar-SA', { hour: '2-digit', minute: '2-digit' })}</span>
      </div>
      ${order.invoice_number ? `<p style="text-align:center;margin:0 0 6px;font-weight:600;">${t('invoice.invoiceNumber')}: ${order.invoice_number}</p>` : ''}
      <table style="margin-bottom:6px;">
        <thead><tr>
          <th style="text-align:right;border-bottom:1px solid #000;padding:3px 0;">${t('pos.item')}</th>
          <th style="text-align:center;border-bottom:1px solid #000;padding:3px 0;width:14%;">${t('pos.qty')}</th>
          <th style="text-align:center;border-bottom:1px solid #000;padding:3px 0;width:20%;">${t('pos.price')}</th>
          <th style="text-align:left;border-bottom:1px solid #000;padding:3px 0;width:22%;">${t('pos.total')}</th>
        </tr></thead>
        <tbody>${itemsRows}</tbody>
      </table>
      <div style="border-top:2px dashed #000;padding-top:6px;margin-bottom:6px;">
        ${row(t('pos.subtotal'), fmt(order.subtotal) + ' ' + sar)}
        ${order.discount_amount > 0 ? row(t('pos.discount'), '-' + fmt(order.discount_amount) + ' ' + sar) : ''}
        ${order.rounding_adjustment > 0 ? row(t('pos.rounding'), '-' + fmt(order.rounding_adjustment) + ' ' + sar) : ''}
        ${row(t('pos.vatIncluded'), fmt(order.vat_amount) + ' ' + sar)}
        <div style="display:flex;justify-content:space-between;border-top:2px solid #000;margin-top:4px;padding-top:4px;font-weight:700;font-size:${totalFont}px;">
          <span>${t('pos.total')}</span><span>${fmt(order.total)} ${sar}</span>
        </div>
      </div>
      <div style="border-top:1px dashed #000;padding-top:5px;margin-bottom:6px;">
        ${row(t('pos.paymentMethod'), order.payment_method === 'cash' ? t('pos.cash') : order.payment_method === 'card' ? t('pos.card') : t('pos.transfer'))}
        ${order.payment_method === 'cash' && order.change > 0 ? row(t('pos.amountPaid'), fmt(order.amount_paid) + ' ' + sar) + row(t('pos.change'), fmt(order.change) + ' ' + sar) : ''}
      </div>
      ${qrSvg ? `<div style="text-align:center;border-top:1px dashed #000;border-bottom:1px dashed #000;padding:8px 0;margin-bottom:6px;">${qrSvg}</div>` : ''}
      <div style="text-align:center;font-size:${f - 2}px;">
        <p style="margin:0;font-weight:600;">${t('pos.receiptThankYou')}</p>
        <p style="margin:2px 0 0;">${t('pos.receiptPoweredBy')}</p>
      </div>
    </div>`;
    printHtmlInFrame(buildPrintDoc(body, paperWidth));
    if (autoKitchen) setTimeout(() => handlePrintKitchen(), 1500);
  };
  const handlePrintKitchen = () => {
    const w = paperWidth === 58 ? 58 : 80;
    const itemFont = w === 58 ? 14 : 18;
    const orderFont = w === 58 ? 18 : 24;
    const orderDate2 = new Date(order.created_at);
    const typeLabel = order.order_type === 'dine_in' ? t('pos.dineIn') : order.order_type === 'delivery' ? t('pos.delivery') : t('pos.pickup');
    const tableInfo = order.order_type === 'dine_in' && order.table_number ? ` — ${t('pos.tableNumber')} ${order.table_number}` : '';
    const itemsHtml = (order.items || []).map(it => `
      <div style="display:flex;gap:8px;align-items:flex-start;padding:6px 0;border-bottom:1px dashed #000;">
        <span style="font-weight:800;white-space:nowrap;">${it.quantity}×</span>
        <span style="font-weight:800;flex:1;">${it.name_ar || it.name}</span>
      </div>
      ${it.special_instructions ? `<p style="margin:2px 0 4px;padding-inline-start:22px;">← ${it.special_instructions}</p>` : ''}`).join('');
    const headerName = businessNameAr || branch?.name || '';
    const branchLine = businessNameAr && branch?.name ? branch.name : '';
    const body = `
      <div class="print-receipt kitchen-ticket" dir="rtl">
        <p style="text-align:center;font-weight:800;margin:0 0 2px;">${t('pos.kitchenTicket')}</p>
        ${headerName ? `<p style="text-align:center;font-weight:700;margin:0 0 2px;">${headerName}</p>` : ''}
        ${branchLine ? `<p style="text-align:center;margin:0 0 6px;font-size:0.85em;">${branchLine}</p>` : ''}
        <div style="border-top:2px dashed #000;border-bottom:2px dashed #000;padding:5px 0;margin-bottom:6px;text-align:center;">
          <p class="kt-order" style="font-weight:800;margin:0;">#${order.order_number}</p>
          <p style="margin:3px 0 0;">${typeLabel}${tableInfo} · ${orderDate2.toLocaleTimeString('ar-SA', { hour: '2-digit', minute: '2-digit' })}</p>
        </div>
        <div>${itemsHtml}</div>
        ${order.notes ? `<div style="margin-top:10px;border:2px solid #000;padding:5px 7px;"><b>${t('pos.notes')}:</b> ${order.notes}</div>` : ''}
      </div>`;
    const css = `
        .kitchen-ticket, .kitchen-ticket p, .kitchen-ticket span, .kitchen-ticket div, .kitchen-ticket b { font-size: ${itemFont}px !important; line-height: 1.5 !important; }
        .kitchen-ticket .kt-order { font-size: ${orderFont}px !important; }`;
    printHtmlInFrame(buildPrintDoc(body, paperWidth, css));
  };

  if (!order) return null;
  const orderDate = new Date(order.created_at);
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-[360px] w-full max-h-[90vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()} data-testid="pos-receipt-modal">
        {/* Success Header */}
        <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 p-5 text-center text-white rounded-t-2xl no-print">
          <CheckCircle2 className="w-12 h-12 mx-auto mb-2" />
          <h2 className="text-lg font-bold">{t('pos.orderComplete')}</h2>
        </div>
        {/* Thermal Receipt Body */}
        <div className="px-5 py-4 text-black print-receipt" ref={receiptRef} dir="rtl" style={{ fontFamily: "'IBM Plex Sans Arabic', monospace" }}>
          {/* Store Header */}
          <div className="text-center pb-3 mb-3" style={{ borderBottom: '2px dashed #d1d5db' }}>
            {logoUrl ? (
              <div className="w-14 h-14 mx-auto mb-2 rounded-xl overflow-hidden bg-white border border-gray-200 flex items-center justify-center">
                <img src={logoUrl} alt="logo" className="w-full h-full object-contain" referrerPolicy="no-referrer" />
              </div>
            ) : (
              <div className="receipt-logo w-12 h-12 mx-auto mb-2 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
                <ChefHat className="w-6 h-6 text-white" />
              </div>
            )}
            {businessNameAr && (
              <p className="receipt-store-name font-bold text-base" data-testid="receipt-business-name-ar">{businessNameAr}</p>
            )}
            {businessNameEn && businessNameEn !== businessNameAr && (
              <p className="text-[11px] text-gray-600 mt-0.5" data-testid="receipt-business-name-en">{businessNameEn}</p>
            )}
            {branch?.name && (
              <p className={`text-[11px] mt-0.5 ${businessNameAr ? 'text-gray-500' : 'font-bold text-base receipt-store-name'}`} data-testid="receipt-branch-name">
                {businessNameAr ? `${t('pos.branch')}: ${branch.name}` : branch.name}
              </p>
            )}
            {businessAddress && <p className="text-[10px] text-gray-500 mt-0.5">{businessAddress}{businessCity ? ` - ${businessCity}` : ''}</p>}
            {!businessAddress && branch?.address && <p className="text-[10px] text-gray-500 mt-0.5">{branch.address}</p>}
            {branch?.phone && <p className="text-[10px] text-gray-500">{branch.phone}</p>}
            {vatNumber && <p className="text-[10px] text-gray-400 mt-1" data-testid="receipt-vat-number">VAT: {vatNumber}</p>}
            <p className="text-[11px] font-bold text-black mt-1.5 border border-gray-300 rounded inline-block px-2 py-0.5" data-testid="receipt-invoice-type">{t('pos.simplifiedTaxInvoice')}</p>
          </div>
          {/* Order Info */}
          <div className="flex justify-between text-[11px] text-gray-600 mb-1">
            <span>{t('pos.orderNumber')}</span>
            <span className="font-mono font-bold text-black">#{order.order_number}</span>
          </div>
          <div className="flex justify-between text-[11px] text-gray-600 mb-1">
            <span>{t('pos.cashier')}</span>
            <span>{order.cashier_name}</span>
          </div>
          <div className="flex justify-between text-[11px] text-gray-600 pb-3 mb-3" style={{ borderBottom: '1px dashed #d1d5db' }}>
            <span>{orderDate.toLocaleDateString('ar-SA')}</span>
            <span>{orderDate.toLocaleTimeString('ar-SA', { hour: '2-digit', minute: '2-digit' })}</span>
          </div>
          {order.invoice_number && (
            <div className="text-[10px] text-gray-500 bg-gray-50 rounded-lg px-2.5 py-1.5 mb-3 text-center font-mono">
              {t('invoice.invoiceNumber')}: <span className="font-bold text-black">{order.invoice_number}</span>
            </div>
          )}
          {/* Items Table */}
          <table className="w-full text-[11px] mb-3">
            <thead>
              <tr className="text-gray-500" style={{ borderBottom: '1px solid #e5e7eb' }}>
                <th className="text-right py-1.5 font-medium">{t('pos.item')}</th>
                <th className="text-center py-1.5 font-medium w-10">{t('pos.qty')}</th>
                <th className="text-center py-1.5 font-medium w-16">{t('pos.price')}</th>
                <th className="text-left py-1.5 font-medium w-16">{t('pos.total')}</th>
              </tr>
            </thead>
            <tbody>
              {order.items?.map((item, i) => (
                <tr key={i} style={{ borderBottom: '1px dotted #e5e7eb' }}>
                  <td className="text-right py-1.5 text-black">{item.name_ar || item.name}</td>
                  <td className="text-center py-1.5 text-gray-600">{item.quantity}</td>
                  <td className="text-center py-1.5 text-gray-600">{item.price != null ? Number(item.price).toFixed(2) : '-'}</td>
                  <td className="text-left py-1.5 font-medium text-black">{Number(item.line_total || 0).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {/* Totals */}
          <div className="space-y-1.5 text-[11px] pt-3 mb-3" style={{ borderTop: '2px dashed #d1d5db' }}>
            <div className="flex justify-between text-gray-600">
              <span>{t('pos.subtotal')}</span>
              <span>{Number(order.subtotal || 0).toFixed(2)} {t('common.sar')}</span>
            </div>
            {order.discount_amount > 0 && (
              <div className="flex justify-between text-red-500 font-medium">
                <span>{t('pos.discount')}</span>
                <span>-{Number(order.discount_amount).toFixed(2)} {t('common.sar')}</span>
              </div>
            )}
            {order.rounding_adjustment > 0 && (
              <div className="flex justify-between text-red-500 font-medium">
                <span>{t('pos.rounding')}</span>
                <span>-{Number(order.rounding_adjustment).toFixed(2)} {t('common.sar')}</span>
              </div>
            )}
            <div className="flex justify-between text-gray-600">
              <span>{t('pos.vatIncluded')}</span>
              <span>{Number(order.vat_amount || 0).toFixed(2)} {t('common.sar')}</span>
            </div>
            <div className="flex justify-between font-bold text-sm pt-2 text-black receipt-total" style={{ borderTop: '2px solid #111' }}>
              <span>{t('pos.total')}</span>
              <span>{Number(order.total || 0).toFixed(2)} {t('common.sar')}</span>
            </div>
          </div>
          {/* Payment Info */}
          <div className="bg-gray-50 rounded-lg px-3 py-2 text-[11px] mb-3 space-y-1">
            <div className="flex justify-between text-gray-600">
              <span>{t('pos.paymentMethod')}</span>
              <span className="font-medium text-black">
                {order.payment_method === 'cash' ? t('pos.cash') : order.payment_method === 'card' ? t('pos.card') : t('pos.transfer')}
              </span>
            </div>
            {order.payment_method === 'cash' && order.change > 0 && (
              <>
                <div className="flex justify-between text-gray-600">
                  <span>{t('pos.amountPaid')}</span>
                  <span>{Number(order.amount_paid || 0).toFixed(2)} {t('common.sar')}</span>
                </div>
                <div className="flex justify-between font-medium text-emerald-600">
                  <span>{t('pos.change')}</span>
                  <span>{Number(order.change || 0).toFixed(2)} {t('common.sar')}</span>
                </div>
              </>
            )}
          </div>
          {/* QR Code */}
          <div className="text-center pt-2 pb-3 mb-3" style={{ borderTop: '1px dashed #d1d5db', borderBottom: '1px dashed #d1d5db' }}>
            <div className="receipt-qr inline-block p-2 bg-white border border-gray-200 rounded-lg">
              <QRCode value={order.zatca_qr || order.id} size={84} />
            </div>
          </div>
          {/* Footer */}
          <div className="text-center text-[10px] text-gray-400 space-y-1">
            <p className="font-medium text-gray-500">{t('pos.receiptThankYou')}</p>
            <p>{t('pos.receiptPoweredBy')}</p>
          </div>
        </div>
        {/* Action Buttons */}
        <div className="px-4 pt-3 no-print">
          <div className="flex items-center justify-center gap-2 mb-1">
            <span className="text-xs text-gray-500">{t('pos.paperSize')}:</span>
            <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden" data-testid="pos-paper-size-toggle">
              <button
                type="button"
                onClick={() => setPaperWidth(80)}
                className={`px-3 py-1 text-xs font-medium transition-colors ${paperWidth === 80 ? 'bg-amber-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                data-testid="pos-paper-80mm"
              >80mm</button>
              <button
                type="button"
                onClick={() => setPaperWidth(58)}
                className={`px-3 py-1 text-xs font-medium transition-colors ${paperWidth === 58 ? 'bg-amber-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                data-testid="pos-paper-58mm"
              >58mm</button>
            </div>
          </div>
          <label className="flex items-center justify-center gap-2 mt-1.5 cursor-pointer select-none" data-testid="pos-auto-kitchen-toggle">
            <input type="checkbox" checked={autoKitchen} onChange={toggleAutoKitchen}
              className="w-3.5 h-3.5 rounded border-gray-300 accent-amber-500" data-testid="pos-auto-kitchen-checkbox" />
            <span className="text-xs text-gray-600">{t('pos.autoKitchenPrint')}</span>
          </label>
        </div>
        <div className="p-4 border-t border-gray-100 space-y-2 bg-gray-50 rounded-b-2xl no-print">
          <div className="flex gap-2">
            <Button onClick={handlePrint} variant="outline" className="flex-1 gap-2 h-10 rounded-xl text-xs" data-testid="pos-print-receipt">
              <Printer className="w-4 h-4" />{t('pos.printReceipt')}
            </Button>
            <Button onClick={handlePrintKitchen} variant="outline" className="flex-1 gap-2 h-10 rounded-xl text-xs border-amber-300 text-amber-700 hover:bg-amber-50 hover:text-amber-800" data-testid="pos-print-kitchen-ticket">
              <ChefHat className="w-4 h-4" />{t('pos.printKitchenTicket')}
            </Button>
          </div>
          <Button onClick={onNewOrder} className="w-full gap-2 h-10 rounded-xl text-xs bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700" data-testid="pos-new-order-btn">
            <RotateCcw className="w-4 h-4" />{t('pos.newOrder')}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ===== PAYMENT MODAL =====
function PaymentModal({ cart, subtotal, discountAmount, vat, total, onConfirm, onClose, t, loading }) {
  const [method, setMethod] = useState('cash');
  const [amountPaid, setAmountPaid] = useState('');
  const change = method === 'cash' ? Math.max((parseFloat(amountPaid) || 0) - total, 0) : 0;
  const canConfirm = method !== 'cash' || (parseFloat(amountPaid) || 0) >= total;
  const quickAmounts = [50, 100, 200, 500];
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <Card className="max-w-lg w-full p-0 overflow-hidden" onClick={e => e.stopPropagation()} data-testid="pos-payment-modal">
        <div className="bg-gradient-to-r from-amber-500 to-orange-600 p-5 text-white text-center">
          <p className="text-sm opacity-80">{t('pos.total')}</p>
          <p className="text-4xl font-bold">{total.toFixed(2)} <span className="text-lg">{t('common.sar')}</span></p>
        </div>
        <div className="p-5 space-y-5">
          <div>
            <p className="text-sm font-medium mb-2">{t('pos.paymentMethod')}</p>
            <div className="grid grid-cols-3 gap-2">
              {[{ key: 'cash', icon: Banknote, label: t('pos.cash') }, { key: 'card', icon: CreditCard, label: t('pos.card') }, { key: 'transfer', icon: Building2, label: t('pos.transfer') }].map(m => (
                <button key={m.key} onClick={() => setMethod(m.key)} data-testid={`pos-pay-${m.key}`}
                  className={`p-3 rounded-xl border-2 transition-all flex flex-col items-center gap-1.5 ${method === m.key ? 'border-amber-500 bg-amber-50 dark:bg-amber-500/10' : 'border-border hover:border-amber-300'}`}>
                  <m.icon className={`w-6 h-6 ${method === m.key ? 'text-amber-600' : 'text-muted-foreground'}`} />
                  <span className={`text-xs font-medium ${method === m.key ? 'text-amber-700 dark:text-amber-400' : 'text-muted-foreground'}`}>{m.label}</span>
                </button>
              ))}
            </div>
          </div>
          {method === 'cash' && (
            <div className="space-y-3">
              <label className="text-sm font-medium">{t('pos.amountPaid')}</label>
              <Input type="number" value={amountPaid} onChange={e => setAmountPaid(e.target.value)}
                placeholder="0.00" className="text-center text-2xl h-14" data-testid="pos-amount-paid" autoFocus />
              <div className="flex gap-2">
                {quickAmounts.map(amt => (
                  <button key={amt} onClick={() => setAmountPaid(String(amt))}
                    className="flex-1 py-2 rounded-lg bg-muted hover:bg-muted/80 text-sm font-medium transition-colors">{amt}</button>
                ))}
              </div>
              {parseFloat(amountPaid) > 0 && (
                <div className="bg-emerald-50 dark:bg-emerald-500/10 rounded-xl p-4 text-center">
                  <p className="text-xs text-emerald-600">{t('pos.change')}</p>
                  <p className="text-3xl font-bold text-emerald-600">{change.toFixed(2)} {t('common.sar')}</p>
                </div>
              )}
            </div>
          )}
          <Button onClick={() => onConfirm(method, parseFloat(amountPaid) || total)} disabled={!canConfirm || loading}
            className="w-full h-12 text-base bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700" data-testid="pos-confirm-payment-btn">
            {loading
              ? <><Loader2 className="w-5 h-5 me-2 animate-spin" />{t('pos.processing')}</>
              : <><CheckCircle2 className="w-5 h-5 me-2" />{t('pos.confirmPayment')}</>}
          </Button>
        </div>
      </Card>
    </div>
  );
}

// ===== CLOSE SHIFT MODAL =====
function CloseShiftModal({ shift, onConfirm, onClose, t }) {
  const [actualCash, setActualCash] = useState('');
  const [notes, setNotes] = useState('');
  const expected = (shift?.opening_cash || 0) + (shift?.cash_sales || 0);
  const diff = (parseFloat(actualCash) || 0) - expected;
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <Card className="max-w-md w-full p-6 space-y-4" onClick={e => e.stopPropagation()} data-testid="pos-close-shift-modal">
        <h2 className="text-lg font-bold">{t('pos.closeShift')}</h2>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="bg-muted/50 p-3 rounded-lg"><p className="text-muted-foreground text-xs">{t('pos.totalOrders')}</p><p className="font-bold text-lg">{shift?.total_orders || 0}</p></div>
          <div className="bg-muted/50 p-3 rounded-lg"><p className="text-muted-foreground text-xs">{t('pos.totalSales')}</p><p className="font-bold text-lg">{(shift?.total_sales || 0).toFixed(2)}</p></div>
          <div className="bg-muted/50 p-3 rounded-lg"><p className="text-muted-foreground text-xs">{t('pos.cashSales')}</p><p className="font-bold">{(shift?.cash_sales || 0).toFixed(2)}</p></div>
          <div className="bg-muted/50 p-3 rounded-lg"><p className="text-muted-foreground text-xs">{t('pos.cardSales')}</p><p className="font-bold">{(shift?.card_sales || 0).toFixed(2)}</p></div>
        </div>
        <div className="bg-amber-50 dark:bg-amber-500/10 p-3 rounded-lg text-sm">
          <p className="text-muted-foreground">{t('pos.expectedCash')}</p>
          <p className="font-bold text-lg">{expected.toFixed(2)} {t('common.sar')}</p>
        </div>
        <div>
          <label className="text-sm font-medium">{t('pos.actualCash')}</label>
          <Input type="number" value={actualCash} onChange={e => setActualCash(e.target.value)}
            placeholder="0.00" className="mt-1 text-center text-xl h-12" data-testid="pos-actual-cash" />
        </div>
        {actualCash && (
          <div className={`p-3 rounded-lg text-center ${diff >= 0 ? 'bg-emerald-50 dark:bg-emerald-500/10' : 'bg-red-50 dark:bg-red-500/10'}`}>
            <p className="text-xs text-muted-foreground">{t('pos.difference')}</p>
            <p className={`font-bold text-xl ${diff >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{diff >= 0 ? '+' : ''}{diff.toFixed(2)} {t('common.sar')}</p>
          </div>
        )}
        <div>
          <label className="text-sm font-medium">{t('pos.shiftNotes')}</label>
          <Input value={notes} onChange={e => setNotes(e.target.value)} className="mt-1" placeholder="..." />
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onClose} className="flex-1">{t('common.cancel')}</Button>
          <Button onClick={() => onConfirm(parseFloat(actualCash) || 0, notes)} className="flex-1 bg-red-500 hover:bg-red-600" data-testid="pos-confirm-close-shift">{t('pos.closeShift')}</Button>
        </div>
      </Card>
    </div>
  );
}

// ===== SHIFT REPORT MODAL =====
function ShiftReportModal({ report, branch, businessSettings, onClose, t }) {
  const paperWidth = branch?.receipt_paper_width || 80;
  const fmt = (v) => Number(v || 0).toFixed(2);
  const sar = t('common.sar');
  const businessNameAr = businessSettings?.business_name_ar || '';
  const diff = report.cash_difference || 0;

  const handlePrintReport = () => {
    const f = paperWidth === 58 ? 11 : 13;
    const titleFont = paperWidth === 58 ? 14 : 17;
    const opened = report.opened_at ? new Date(report.opened_at) : null;
    const closed = report.closed_at ? new Date(report.closed_at) : null;
    const dt = (d) => d ? `${d.toLocaleDateString('ar-SA')} ${d.toLocaleTimeString('ar-SA', { hour: '2-digit', minute: '2-digit' })}` : '-';
    const row = (label, value, bold = false) => `<div style="display:flex;justify-content:space-between;margin:2px 0;${bold ? 'font-weight:700;' : ''}"><span>${label}</span><span>${value}</span></div>`;
    const topRows = (report.top_items || []).map(it => `
      <tr>
        <td style="text-align:right;padding:2px 0;border-bottom:1px dotted #999;">${it.name}</td>
        <td style="text-align:center;padding:2px 0;border-bottom:1px dotted #999;">${it.qty}</td>
        <td style="text-align:left;padding:2px 0;border-bottom:1px dotted #999;">${fmt(it.total)}</td>
      </tr>`).join('');
    const body = `
    <div class="print-receipt" dir="rtl" style="font-size:${f}px;line-height:1.6;">
      <div style="text-align:center;border-bottom:2px dashed #000;padding-bottom:8px;margin-bottom:8px;">
        ${businessNameAr ? `<p style="margin:0;font-size:${titleFont}px;font-weight:700;">${businessNameAr}</p>` : ''}
        ${branch?.name ? `<p style="margin:2px 0 0;font-size:${f - 2}px;">${branch.name}</p>` : ''}
        <p style="margin:6px auto 0;display:inline-block;border:2px solid #000;padding:2px 10px;font-weight:700;font-size:${f + 1}px;">${t('pos.shiftReport')}</p>
      </div>
      ${row(t('pos.cashier'), report.cashier_name || '')}
      ${row(t('pos.openedAt'), dt(opened))}
      ${row(t('pos.closedAt'), dt(closed))}
      <div style="border-top:2px dashed #000;margin-top:6px;padding-top:6px;">
        ${row(t('pos.totalOrders'), report.total_orders || 0)}
        ${row(t('pos.totalSales'), fmt(report.total_sales) + ' ' + sar, true)}
        ${row(t('pos.cashSales'), fmt(report.cash_sales) + ' ' + sar)}
        ${row(t('pos.cardSales'), fmt(report.card_sales) + ' ' + sar)}
        ${row(t('pos.transferSales'), fmt(report.transfer_sales) + ' ' + sar)}
        ${(report.total_refunds || 0) > 0 ? row(t('pos.refunds'), '-' + fmt(report.total_refunds) + ' ' + sar, true) : ''}
        ${row(t('pos.totalDiscounts'), fmt(report.total_discount) + ' ' + sar)}
        ${row(t('pos.totalVat'), fmt(report.total_vat) + ' ' + sar)}
      </div>
      <div style="border:2px solid #000;padding:5px 7px;margin:8px 0;">
        ${row(t('pos.openingCash'), fmt(report.opening_cash) + ' ' + sar)}
        ${row(t('pos.expectedCash'), fmt(report.expected_cash) + ' ' + sar)}
        ${row(t('pos.actualCash'), fmt(report.actual_cash) + ' ' + sar)}
        ${row(t('pos.difference'), (diff >= 0 ? '+' : '') + fmt(diff) + ' ' + sar, true)}
      </div>
      ${(report.top_items || []).length ? `
      <p style="margin:6px 0 3px;font-weight:700;">${t('pos.topItems')}</p>
      <table style="margin-bottom:6px;">
        <thead><tr>
          <th style="text-align:right;border-bottom:1px solid #000;padding:2px 0;">${t('pos.item')}</th>
          <th style="text-align:center;border-bottom:1px solid #000;padding:2px 0;width:18%;">${t('pos.qty')}</th>
          <th style="text-align:left;border-bottom:1px solid #000;padding:2px 0;width:26%;">${t('pos.total')}</th>
        </tr></thead><tbody>${topRows}</tbody>
      </table>` : ''}
      ${report.close_notes ? `<p style="margin:4px 0;"><b>${t('pos.shiftNotes')}:</b> ${report.close_notes}</p>` : ''}
      <div style="display:flex;justify-content:space-between;gap:10px;margin-top:24px;">
        <div style="flex:1;text-align:center;border-top:1px solid #000;padding-top:3px;font-size:${f - 2}px;">${t('pos.cashierSignature')}</div>
        <div style="flex:1;text-align:center;border-top:1px solid #000;padding-top:3px;font-size:${f - 2}px;">${t('pos.managerSignature')}</div>
      </div>
    </div>`;
    printHtmlInFrame(buildPrintDoc(body, paperWidth));
  };

  useEffect(() => {
    const timer = setTimeout(handlePrintReport, 700);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <Card className="max-w-md w-full p-6 space-y-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="pos-shift-report-modal">
        <div className="text-center">
          <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-500 mb-1" />
          <h2 className="text-lg font-bold">{t('pos.shiftReport')}</h2>
          <p className="text-xs text-muted-foreground">{report.cashier_name}</p>
        </div>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="bg-muted/50 p-3 rounded-lg"><p className="text-muted-foreground text-xs">{t('pos.totalOrders')}</p><p className="font-bold text-lg">{report.total_orders || 0}</p></div>
          <div className="bg-muted/50 p-3 rounded-lg"><p className="text-muted-foreground text-xs">{t('pos.totalSales')}</p><p className="font-bold text-lg">{fmt(report.total_sales)}</p></div>
          <div className="bg-muted/50 p-3 rounded-lg"><p className="text-muted-foreground text-xs">{t('pos.expectedCash')}</p><p className="font-bold">{fmt(report.expected_cash)}</p></div>
          <div className="bg-muted/50 p-3 rounded-lg"><p className="text-muted-foreground text-xs">{t('pos.actualCash')}</p><p className="font-bold">{fmt(report.actual_cash)}</p></div>
        </div>
        <div className={`p-3 rounded-lg text-center ${diff >= 0 ? 'bg-emerald-50 dark:bg-emerald-500/10' : 'bg-red-50 dark:bg-red-500/10'}`}>
          <p className="text-xs text-muted-foreground">{t('pos.difference')}</p>
          <p className={`font-bold text-xl ${diff >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{diff >= 0 ? '+' : ''}{fmt(diff)} {t('common.sar')}</p>
        </div>
        {(report.total_refunds || 0) > 0 && (
          <div className="flex justify-between items-center bg-red-50 dark:bg-red-500/10 rounded-lg px-3 py-2 text-sm">
            <span className="text-muted-foreground">{t('pos.refunds')} ({report.refund_count || 0})</span>
            <span className="font-bold text-red-600">-{fmt(report.total_refunds)} {t('common.sar')}</span>
          </div>
        )}
        {(report.top_items || []).length > 0 && (
          <div>
            <p className="text-sm font-semibold mb-1.5">{t('pos.topItems')}</p>
            <div className="space-y-1">
              {report.top_items.map((it, i) => (
                <div key={i} className="flex justify-between text-xs bg-muted/40 rounded-md px-2.5 py-1.5">
                  <span>{it.name}</span><span className="font-medium">×{it.qty}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="flex gap-2">
          <Button onClick={handlePrintReport} variant="outline" className="flex-1 gap-2" data-testid="pos-print-shift-report">
            <Printer className="w-4 h-4" />{t('pos.printReport')}
          </Button>
          <Button onClick={onClose} className="flex-1 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700" data-testid="pos-shift-report-close">{t('common.close')}</Button>
        </div>
      </Card>
    </div>
  );
}

// ===== RETURNS MODAL =====
function ReturnsModal({ branch, businessSettings, onClose, onDone, t, lang, API, authHeaders }) {
  const [query, setQuery] = useState('');
  const [orders, setOrders] = useState([]);
  const [selected, setSelected] = useState(null);
  const [returnQty, setReturnQty] = useState({});
  const [reason, setReason] = useState('');
  const [reasonText, setReasonText] = useState('');
  const [refundMethod, setRefundMethod] = useState('cash');
  const [submitting, setSubmitting] = useState(false);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [createdCN, setCreatedCN] = useState(null);
  const cnQrRef = useRef(null);
  const paperWidth = branch?.receipt_paper_width || 80;
  const fmt = (v) => Number(v || 0).toFixed(2);
  const sar = t('common.sar');

  const fetchOrders = async (q = '') => {
    setLoadingOrders(true);
    try {
      const res = await axios.get(`${API}/pos/orders/search?branch_id=${branch.id}&q=${encodeURIComponent(q)}`, authHeaders());
      setOrders(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || t('common.failed')); }
    finally { setLoadingOrders(false); }
  };
  useEffect(() => {
    const tm = setTimeout(() => fetchOrders(query), 350);
    return () => clearTimeout(tm);
  }, [query]);

  const remainingOf = (o, it) => it.quantity - (o.returned_quantities?.[it.item_id] || 0);
  const selectOrder = (o) => { setSelected(o); setReturnQty({}); setRefundMethod(o.payment_method === 'card' ? 'card' : 'cash'); };
  const setQty = (itemId, val, max) => setReturnQty(prev => ({ ...prev, [itemId]: Math.max(0, Math.min(max, val)) }));

  const ratio = selected?.subtotal ? selected.total / selected.subtotal : 1;
  const refundPreview = selected ? Object.entries(returnQty).reduce((sum, [id, q]) => {
    const it = selected.items.find(i => i.item_id === id);
    return sum + (it ? it.price * q : 0);
  }, 0) * ratio : 0;

  const reasonOptions = [
    { key: 'wrong', label: t('pos.reasonWrongOrder') },
    { key: 'quality', label: t('pos.reasonQuality') },
    { key: 'customer', label: t('pos.reasonCustomer') },
    { key: 'other', label: t('pos.reasonOther') },
  ];

  const printCreditNote = (cn) => {
    const f = paperWidth === 58 ? 11 : 13;
    const titleFont = paperWidth === 58 ? 14 : 17;
    const d = new Date(cn.created_at);
    const businessNameAr = businessSettings?.business_name_ar || '';
    const vatNumber = businessSettings?.vat_number || '';
    const qrSvg = cnQrRef.current?.querySelector('svg')?.outerHTML || '';
    const row = (label, value, bold = false) => `<div style="display:flex;justify-content:space-between;margin:2px 0;${bold ? 'font-weight:700;' : ''}"><span>${label}</span><span>${value}</span></div>`;
    const itemsRows = (cn.items || []).map(it => `
      <tr>
        <td style="text-align:right;padding:3px 0;border-bottom:1px dotted #999;">${it.name_ar || it.name}</td>
        <td style="text-align:center;padding:3px 0;border-bottom:1px dotted #999;">${it.quantity}</td>
        <td style="text-align:left;padding:3px 0;border-bottom:1px dotted #999;font-weight:600;">${fmt(it.line_total)}</td>
      </tr>`).join('');
    const body = `
    <div class="print-receipt" dir="rtl" style="font-size:${f}px;line-height:1.55;">
      <div style="text-align:center;border-bottom:2px dashed #000;padding-bottom:8px;margin-bottom:8px;">
        ${businessNameAr ? `<p style="margin:0;font-size:${titleFont}px;font-weight:700;">${businessNameAr}</p>` : ''}
        ${branch?.name ? `<p style="margin:2px 0 0;font-size:${f - 2}px;">${branch.name}</p>` : ''}
        ${vatNumber ? `<p style="margin:2px 0 0;font-size:${f - 3}px;">VAT: ${vatNumber}</p>` : ''}
        <p style="margin:6px auto 0;display:inline-block;border:2px solid #000;padding:2px 10px;font-weight:700;font-size:${f + 1}px;">${t('pos.creditNote')}</p>
      </div>
      ${row(t('pos.creditNoteNumber'), cn.cn_number, true)}
      ${cn.invoice_number ? row(t('pos.refInvoice'), cn.invoice_number) : ''}
      ${row(t('pos.orderNumber'), '#' + cn.order_number)}
      ${row(t('pos.cashier'), cn.cashier_name || '')}
      <div style="display:flex;justify-content:space-between;border-bottom:1px dashed #000;padding-bottom:6px;margin-bottom:6px;">
        <span>${d.toLocaleDateString('ar-SA')}</span><span>${d.toLocaleTimeString('ar-SA', { hour: '2-digit', minute: '2-digit' })}</span>
      </div>
      <table style="margin-bottom:6px;">
        <thead><tr>
          <th style="text-align:right;border-bottom:1px solid #000;padding:3px 0;">${t('pos.item')}</th>
          <th style="text-align:center;border-bottom:1px solid #000;padding:3px 0;width:16%;">${t('pos.qty')}</th>
          <th style="text-align:left;border-bottom:1px solid #000;padding:3px 0;width:26%;">${t('pos.total')}</th>
        </tr></thead>
        <tbody>${itemsRows}</tbody>
      </table>
      <div style="border-top:2px dashed #000;padding-top:6px;margin-bottom:6px;">
        ${row(t('pos.vatIncluded'), fmt(cn.refund_vat) + ' ' + sar)}
        <div style="display:flex;justify-content:space-between;border-top:2px solid #000;margin-top:4px;padding-top:4px;font-weight:700;font-size:${paperWidth === 58 ? 13 : 16}px;">
          <span>${t('pos.refundTotal')}</span><span>${fmt(cn.refund_total)} ${sar}</span>
        </div>
        ${row(t('pos.refundMethod'), cn.refund_method === 'cash' ? t('pos.cash') : cn.refund_method === 'card' ? t('pos.card') : t('pos.transfer'))}
        ${row(t('pos.returnReason'), cn.reason)}
      </div>
      ${qrSvg ? `<div style="text-align:center;border-top:1px dashed #000;border-bottom:1px dashed #000;padding:8px 0;margin-bottom:6px;">${qrSvg}</div>` : ''}
      <div style="text-align:center;font-size:${f - 2}px;">
        <p style="margin:0;">${t('pos.receiptPoweredBy')}</p>
      </div>
    </div>`;
    printHtmlInFrame(buildPrintDoc(body, paperWidth));
  };

  useEffect(() => {
    if (createdCN) {
      const tm = setTimeout(() => printCreditNote(createdCN), 600);
      return () => clearTimeout(tm);
    }
  }, [createdCN]);

  const handleConfirm = async () => {
    const items = Object.entries(returnQty).filter(([, q]) => q > 0).map(([item_id, quantity]) => ({ item_id, quantity }));
    if (!items.length) { toast.error(t('pos.selectReturnItems')); return; }
    const reasonLabel = reasonOptions.find(r => r.key === reason)?.label || '';
    const finalReason = reason === 'other' ? (reasonText.trim() || reasonLabel) : reasonLabel;
    if (!finalReason) { toast.error(t('pos.returnReasonRequired')); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/pos/returns`, { order_id: selected.id, items, reason: finalReason, refund_method: refundMethod }, authHeaders());
      toast.success(t('pos.returnSuccess') + ' - ' + res.data.cn_number);
      setCreatedCN(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || t('common.failed')); }
    finally { setSubmitting(false); }
  };

  if (createdCN) {
    return (
      <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
        <Card className="max-w-sm w-full p-6 space-y-4 text-center" data-testid="pos-credit-note-success">
          <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-500" />
          <div>
            <h2 className="text-lg font-bold">{t('pos.creditNote')}</h2>
            <p className="text-sm font-mono font-semibold text-orange-600" data-testid="pos-cn-number">{createdCN.cn_number}</p>
          </div>
          <div ref={cnQrRef} className="flex justify-center"><div className="p-2 bg-white border border-gray-200 rounded-lg"><QRCode value={createdCN.zatca_qr} size={90} /></div></div>
          <div className="text-sm space-y-1">
            <div className="flex justify-between"><span className="text-muted-foreground">{t('pos.refInvoice')}</span><span>{createdCN.invoice_number}</span></div>
            <div className="flex justify-between font-bold"><span>{t('pos.refundTotal')}</span><span className="text-red-600">{fmt(createdCN.refund_total)} {sar}</span></div>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => printCreditNote(createdCN)} variant="outline" className="flex-1 gap-2" data-testid="pos-print-credit-note">
              <Printer className="w-4 h-4" />{t('pos.printReport')}
            </Button>
            <Button onClick={() => { onDone(); onClose(); }} className="flex-1 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700" data-testid="pos-cn-close">{t('common.close')}</Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <Card className="max-w-2xl w-full p-5 space-y-3 max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="pos-returns-modal">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold flex items-center gap-2"><RotateCcw className="w-5 h-5 text-orange-500" />{t('pos.returnOrder')}</h2>
          <Button variant="ghost" size="sm" onClick={onClose} className="h-8 w-8 p-0" data-testid="pos-returns-close"><X className="w-4 h-4" /></Button>
        </div>
        {!selected ? (
          <>
            <div className="relative">
              <Search className="absolute top-1/2 -translate-y-1/2 start-3 w-4 h-4 text-muted-foreground" />
              <Input value={query} onChange={e => setQuery(e.target.value)} placeholder={t('pos.searchOrderPlaceholder')} className="ps-10 h-10" data-testid="pos-returns-search" />
            </div>
            <p className="text-xs text-muted-foreground">{t('pos.sameDayOnly')}</p>
            <div className="space-y-1.5 max-h-80 overflow-y-auto">
              {loadingOrders ? (
                <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
              ) : orders.length === 0 ? (
                <p className="text-center text-sm text-muted-foreground py-8">{t('pos.noOrdersToday')}</p>
              ) : orders.map(o => {
                const fullyReturned = o.items.every(it => remainingOf(o, it) === 0);
                return (
                  <button key={o.id} onClick={() => !fullyReturned && selectOrder(o)} disabled={fullyReturned}
                    className={`w-full flex items-center justify-between bg-muted/40 rounded-lg px-3 py-2.5 text-start transition-colors ${fullyReturned ? 'opacity-50 cursor-not-allowed' : 'hover:bg-muted'}`}
                    data-testid={`pos-return-order-${o.id}`}>
                    <div>
                      <p className="text-sm font-semibold">#{o.order_number} <span className="text-xs font-normal text-muted-foreground ms-1">{o.invoice_number}</span></p>
                      <p className="text-xs text-muted-foreground">{new Date(o.created_at).toLocaleTimeString(lang === 'ar' ? 'ar-SA' : 'en-US', { hour: '2-digit', minute: '2-digit' })} · {o.payment_method === 'cash' ? t('pos.cash') : o.payment_method === 'card' ? t('pos.card') : t('pos.transfer')}</p>
                    </div>
                    <div className="text-end">
                      <p className="text-sm font-bold">{fmt(o.total)} {sar}</p>
                      {o.total_refunded > 0 && <Badge variant="outline" className="text-[10px] border-red-300 text-red-500">{fullyReturned ? t('pos.fullyReturned') : `${t('pos.returned')} ${fmt(o.total_refunded)}`}</Badge>}
                    </div>
                  </button>
                );
              })}
            </div>
          </>
        ) : (
          <>
            <button onClick={() => setSelected(null)} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1" data-testid="pos-return-back">
              <ArrowRight className="w-3 h-3 rtl:rotate-0 ltr:rotate-180" />{t('pos.todayOrders')} · #{selected.order_number} · {selected.invoice_number}
            </button>
            <div className="space-y-1.5 max-h-56 overflow-y-auto">
              {selected.items.map(it => {
                const max = remainingOf(selected, it);
                const q = returnQty[it.item_id] || 0;
                return (
                  <div key={it.item_id} className={`flex items-center justify-between bg-muted/40 rounded-lg px-3 py-2 ${max === 0 ? 'opacity-50' : ''}`} data-testid={`pos-return-item-${it.item_id}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{lang === 'ar' ? (it.name_ar || it.name) : it.name}</p>
                      <p className="text-xs text-muted-foreground">{fmt(it.price)} {sar} × {it.quantity}{max < it.quantity ? ` · ${t('pos.returned')}: ${it.quantity - max}` : ''}</p>
                    </div>
                    {max === 0 ? (
                      <Badge variant="outline" className="text-[10px] border-red-300 text-red-500">{t('pos.fullyReturned')}</Badge>
                    ) : (
                      <div className="flex items-center gap-1.5">
                        <button onClick={() => setQty(it.item_id, q - 1, max)} className="w-8 h-8 rounded-md bg-muted flex items-center justify-center hover:bg-red-100 dark:hover:bg-red-500/20" data-testid={`pos-return-minus-${it.item_id}`}><Minus className="w-3.5 h-3.5" /></button>
                        <span className="w-7 text-center font-medium">{q}</span>
                        <button onClick={() => setQty(it.item_id, q + 1, max)} className="w-8 h-8 rounded-md bg-muted flex items-center justify-center hover:bg-emerald-100 dark:hover:bg-emerald-500/20" data-testid={`pos-return-plus-${it.item_id}`}><Plus className="w-3.5 h-3.5" /></button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div>
              <p className="text-xs font-medium mb-1.5">{t('pos.returnReason')} *</p>
              <div className="flex flex-wrap gap-1.5">
                {reasonOptions.map(r => (
                  <button key={r.key} onClick={() => setReason(r.key)} data-testid={`pos-return-reason-${r.key}`}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${reason === r.key ? 'bg-orange-500 text-white' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`}>{r.label}</button>
                ))}
              </div>
              {reason === 'other' && <Input value={reasonText} onChange={e => setReasonText(e.target.value)} placeholder={t('pos.returnReason')} className="mt-2 h-9 text-sm" data-testid="pos-return-reason-text" />}
            </div>
            <div>
              <p className="text-xs font-medium mb-1.5">{t('pos.refundMethod')}</p>
              <div className="flex gap-1.5">
                {['cash', 'card'].map(m => (
                  <button key={m} onClick={() => setRefundMethod(m)} data-testid={`pos-refund-method-${m}`}
                    className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all ${refundMethod === m ? 'bg-amber-500 text-white' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`}>
                    {m === 'cash' ? t('pos.cash') : t('pos.card')}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center justify-between bg-red-50 dark:bg-red-500/10 rounded-lg px-3 py-2.5">
              <span className="text-sm font-medium">{t('pos.refundTotal')}</span>
              <span className="text-lg font-bold text-red-600" data-testid="pos-refund-preview">{fmt(refundPreview)} {sar}</span>
            </div>
            <Button onClick={handleConfirm} disabled={submitting || refundPreview <= 0}
              className="w-full h-11 bg-gradient-to-r from-red-500 to-orange-600 hover:from-red-600 hover:to-orange-700" data-testid="pos-confirm-return-btn">
              {submitting ? <><Loader2 className="w-4 h-4 me-2 animate-spin" />{t('pos.processing')}</> : <><RotateCcw className="w-4 h-4 me-2" />{t('pos.confirmReturn')}</>}
            </Button>
          </>
        )}
      </Card>
    </div>
  );
}

// ===== MAIN POS PAGE =====
export default function POSPage() {
  const { authHeaders, API, activeBranch, user, hasPermission } = useAuth();
  const { t } = useTranslation();
  const { dir, lang } = useLanguage();
  const navigate = useNavigate();

  const [shift, setShift] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  const [bestSellers, setBestSellers] = useState([]);
  const [businessSettings, setBusinessSettings] = useState(null);
  const [cart, setCart] = useState([]);
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [orderType, setOrderType] = useState('dine_in');
  const [tableNumber, setTableNumber] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerAddress, setCustomerAddress] = useState('');
  const [notes, setNotes] = useState('');
  const [discountType, setDiscountType] = useState('');
  const [discountValue, setDiscountValue] = useState('');
  const [discountReason, setDiscountReason] = useState('');
  const [showPayment, setShowPayment] = useState(false);
  const [showCloseShift, setShowCloseShift] = useState(false);
  const [showReturns, setShowReturns] = useState(false);
  const [shiftReport, setShiftReport] = useState(null);
  const [completedOrder, setCompletedOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [shiftLoading, setShiftLoading] = useState(true);
  const [dark, setDark] = useState(() => document.documentElement.classList.contains('dark'));
  const [touchMode, setTouchMode] = useState(() => localStorage.getItem('zouqai_pos_touch') === 'true');
  const toggleTouchMode = () => { const n = !touchMode; setTouchMode(n); localStorage.setItem('zouqai_pos_touch', String(n)); };

  const branchId = activeBranch?.id;

  // Load shift, menu, and business settings
  useEffect(() => {
    if (branchId) { checkShift(); fetchMenu(); fetchBusinessSettings(); fetchBestSellers(); }
  }, [branchId]);

  const fetchBestSellers = async () => {
    try {
      const res = await axios.get(`${API}/pos/best-sellers?branch_id=${branchId}`, authHeaders());
      setBestSellers(res.data.map(r => r.item_id));
    } catch { setBestSellers([]); }
  };

  const fetchBusinessSettings = async () => {
    try {
      const res = await axios.get(`${API}/business-settings?branch_id=${branchId}`, authHeaders());
      setBusinessSettings(res.data || null);
    } catch { setBusinessSettings(null); }
  };

  const checkShift = async () => {
    setShiftLoading(true);
    try {
      const res = await axios.get(`${API}/pos/shifts/current?branch_id=${branchId}`, authHeaders());
      setShift(res.data?.id ? res.data : null);
    } catch { setShift(null); }
    finally { setShiftLoading(false); }
  };

  const fetchMenu = async () => {
    try {
      const res = await axios.get(`${API}/menu-items?branch_id=${branchId}`, authHeaders());
      setMenuItems(res.data.filter(i => i.is_available));
    } catch { /* empty */ }
  };

  const openShift = async (openingCash) => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/pos/shifts/open`, { branch_id: branchId, opening_cash: openingCash }, authHeaders());
      setShift(res.data);
      toast.success(t('pos.shiftOpen'));
    } catch (e) { toast.error(e.response?.data?.detail || t('common.failed')); }
    finally { setLoading(false); }
  };

  const closeShift = async (actualCash, closeNotes) => {
    try {
      const res = await axios.post(`${API}/pos/shifts/${shift.id}/close`, { actual_cash: actualCash, notes: closeNotes }, authHeaders());
      setShift(null);
      setShowCloseShift(false);
      setShiftReport(res.data);
      toast.success(t('pos.closeShift') + ' - ' + t('common.success'));
    } catch (e) { toast.error(e.response?.data?.detail || t('common.failed')); }
  };

  // Cart logic
  const addToCart = (item) => {
    setCart(prev => {
      const existing = prev.find(c => c.item_id === item.id);
      if (existing) return prev.map(c => c.item_id === item.id ? { ...c, quantity: c.quantity + 1 } : c);
      return [...prev, { item_id: item.id, name: item.name, name_ar: item.name_ar, price: item.price, quantity: 1 }];
    });
  };
  const updateQty = (itemId, delta) => {
    setCart(prev => prev.map(c => c.item_id === itemId ? { ...c, quantity: Math.max(1, c.quantity + delta) } : c));
  };
  const removeFromCart = (itemId) => {
    setCart(prev => prev.filter(c => c.item_id !== itemId));
  };
  const clearCart = () => { setCart([]); setDiscountType(''); setDiscountValue(''); setDiscountReason(''); setCustomerName(''); setCustomerPhone(''); setCustomerAddress(''); setTableNumber(''); setNotes(''); };

  // Calculations
  const subtotal = useMemo(() => cart.reduce((s, c) => s + c.price * c.quantity, 0), [cart]);
  const discountAmount = useMemo(() => {
    if (discountType === 'percent') return Math.round(subtotal * ((parseFloat(discountValue) || 0) / 100) * 100) / 100;
    if (discountType === 'fixed') return Math.min(parseFloat(discountValue) || 0, subtotal);
    return 0;
  }, [subtotal, discountType, discountValue]);
  const afterDiscount = Math.round((subtotal - discountAmount) * 100) / 100;
  const roundingAdj = discountType === 'percent' && discountAmount > 0
    ? Math.round((afterDiscount - Math.floor(afterDiscount)) * 100) / 100 : 0;
  const total = Math.round((afterDiscount - roundingAdj) * 100) / 100;
  const vat = Math.round((total * 0.15 / 1.15) * 100) / 100;

  // Categories
  const categories = useMemo(() => {
    const cats = [...new Set(menuItems.map(i => i.category))];
    return bestSellers.length > 0 ? ['best', 'all', ...cats] : ['all', ...cats];
  }, [menuItems, bestSellers]);

  const filteredItems = useMemo(() => {
    let items = menuItems;
    if (activeCategory === 'best') {
      const map = new Map(menuItems.map(i => [i.id, i]));
      items = bestSellers.map(id => map.get(id)).filter(Boolean);
    } else if (activeCategory !== 'all') items = items.filter(i => i.category === activeCategory);
    if (search) items = items.filter(i => i.name.toLowerCase().includes(search.toLowerCase()) || (i.name_ar || '').includes(search));
    return items;
  }, [menuItems, activeCategory, search, bestSellers]);

  const categoryLabels = { all: t('pos.allCategories'), best: t('pos.bestSellers'), main: lang === 'ar' ? 'رئيسي' : 'Main', appetizer: lang === 'ar' ? 'مقبلات' : 'Appetizer', dessert: lang === 'ar' ? 'حلويات' : 'Dessert', beverage: lang === 'ar' ? 'مشروبات' : 'Beverage' };

  // Checkout
  const handleConfirmPayment = async (method, amountPaid) => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/pos/orders`, {
        branch_id: branchId, items: cart.map(c => ({ item_id: c.item_id, quantity: c.quantity })),
        order_type: orderType, table_number: parseInt(tableNumber) || 0,
        customer_name: customerName, customer_phone: customerPhone, customer_address: customerAddress,
        payment_method: method, amount_paid: amountPaid,
        discount_type: discountType, discount_value: parseFloat(discountValue) || 0, discount_reason: discountReason,
        notes: notes
      }, authHeaders());
      setCompletedOrder(res.data);
      setShowPayment(false);
      // Refresh shift data
      checkShift();
      fetchBestSellers();
    } catch (e) { toast.error(e.response?.data?.detail || t('common.failed')); }
    finally { setLoading(false); }
  };

  const handleNewOrder = () => {
    setCompletedOrder(null);
    clearCart();
    setOrderType('dine_in');
  };

  const toggleTheme = () => { document.documentElement.classList.toggle('dark'); setDark(!dark); };

  if (shiftLoading) {
    return <div className="min-h-screen bg-background flex items-center justify-center"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;
  }

  if (!shift) {
    return (
      <>
        <ShiftGate shift={shift} onOpenShift={openShift} t={t} loading={loading} onBackToDashboard={() => navigate('/dashboard')} />
        {shiftReport && <ShiftReportModal report={shiftReport} branch={activeBranch} businessSettings={businessSettings} onClose={() => setShiftReport(null)} t={t} />}
      </>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-background overflow-hidden" dir={dir} data-testid="pos-main">
      {/* Top Bar */}
      <header className="h-14 border-b border-border/50 px-4 flex items-center justify-between flex-shrink-0" style={{ background: 'hsl(var(--sidebar-bg))' }}>
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} className="text-white/60 hover:text-white h-8 gap-1.5" data-testid="pos-back-btn">
            {dir === 'rtl' ? <ArrowRight className="w-4 h-4" /> : <ArrowLeft className="w-4 h-4" />}
            <span className="text-xs hidden sm:inline">{t('pos.backToDashboard')}</span>
          </Button>
          <div className="h-6 w-px bg-white/10" />
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
              <Monitor className="w-4 h-4 text-white" />
            </div>
            <span className="font-bold text-white text-sm">{t('pos.title')}</span>
          </div>
        </div>
        <div className="flex items-center gap-2 text-white/60 text-xs">
          <Badge variant="outline" className="border-emerald-500/50 text-emerald-400 text-[10px]">{t('pos.shiftOpen')}</Badge>
          <span className="hidden md:inline">{user?.name}</span>
          <span className="hidden md:inline">|</span>
          <span className="hidden md:inline">{activeBranch?.name}</span>
          <div className="h-6 w-px bg-white/10" />
          <span className="font-medium text-amber-400">{t('pos.todayOrders')}: {shift.total_orders}</span>
          <span className="hidden sm:inline text-amber-400">|</span>
          <span className="hidden sm:inline font-medium text-amber-400">{(shift.total_sales || 0).toFixed(0)} {t('common.sar')}</span>
          <div className="h-6 w-px bg-white/10" />
          <Button variant="ghost" size="sm" onClick={toggleTouchMode} title={t('pos.touchMode')} data-testid="pos-touch-mode-toggle"
            className={`h-7 w-7 p-0 ${touchMode ? 'text-amber-400 hover:text-amber-300 bg-amber-500/10' : 'text-white/40 hover:text-white'}`}>
            <Hand className="w-3.5 h-3.5" />
          </Button>
          <Button variant="ghost" size="sm" onClick={toggleTheme} className="h-7 w-7 p-0 text-white/40 hover:text-white">
            {dark ? <Sun className="w-3.5 h-3.5" /> : <Moon className="w-3.5 h-3.5" />}
          </Button>
          <Button variant="ghost" size="sm"
            onClick={() => hasPermission('pos.refund') ? setShowReturns(true) : toast.error(t('pos.noRefundPermission'))}
            title={t('pos.returns')} data-testid="pos-returns-btn"
            className={`h-7 text-xs gap-1 ${hasPermission('pos.refund') ? 'text-orange-300 hover:text-orange-200 hover:bg-orange-500/10' : 'text-white/20'}`}>
            <RotateCcw className="w-3.5 h-3.5" /><span className="hidden sm:inline">{t('pos.returns')}</span>
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setShowCloseShift(true)} className="h-7 text-red-400 hover:text-red-300 hover:bg-red-500/10 text-xs gap-1" data-testid="pos-close-shift-btn">
            <X className="w-3.5 h-3.5" /><span className="hidden sm:inline">{t('pos.closeShift')}</span>
          </Button>
        </div>
      </header>

      {/* Main content */}
      <div className="flex-1 flex overflow-hidden">
        {/* LEFT: Menu Grid (70%) */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Search + Categories */}
          <div className="p-3 border-b border-border/50 space-y-2">
            <div className="relative">
              <Search className="absolute top-1/2 -translate-y-1/2 start-3 w-4 h-4 text-muted-foreground" />
              <Input value={search} onChange={e => setSearch(e.target.value)} placeholder={t('pos.searchMenu')}
                className={`ps-10 ${touchMode ? 'h-12 text-base' : 'h-10'}`} data-testid="pos-search" />
            </div>
            <div className="flex gap-1.5 overflow-x-auto pb-1 scrollbar-hide">
              {categories.map(cat => (
                <button key={cat} onClick={() => setActiveCategory(cat)} data-testid={`pos-cat-${cat}`}
                  className={`${touchMode ? 'px-5 py-2.5 text-sm' : 'px-3 py-1.5 text-xs'} rounded-full font-medium whitespace-nowrap transition-all flex items-center gap-1 ${activeCategory === cat ? (cat === 'best' ? 'bg-orange-600 text-white' : 'bg-amber-500 text-white') : (cat === 'best' ? 'bg-orange-100 text-orange-700 dark:bg-orange-500/10 dark:text-orange-400 hover:bg-orange-200' : 'bg-muted text-muted-foreground hover:bg-muted/80')}`}>
                  {cat === 'best' && <Flame className={touchMode ? 'w-4 h-4' : 'w-3 h-3'} />}
                  {categoryLabels[cat] || cat}
                </button>
              ))}
            </div>
          </div>
          {/* Product Grid */}
          <div className="flex-1 overflow-y-auto p-3">
            <div className={`grid ${touchMode ? 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3' : 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2'}`}>
              {filteredItems.map(item => (
                <button key={item.id} onClick={() => addToCart(item)} data-testid={`pos-item-${item.id}`}
                  className={`bg-card border border-border/50 rounded-xl ${touchMode ? 'p-4 active:scale-95' : 'p-3'} text-start hover:border-amber-500/50 hover:shadow-md transition-all group`}>
                  {item.image_url && (
                    <div className="w-full aspect-square rounded-lg overflow-hidden mb-2 bg-muted">
                      <img src={item.image_url} alt={item.name_ar || item.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" loading="lazy" />
                    </div>
                  )}
                  <p className={`${touchMode ? 'text-sm min-h-[2.6rem]' : 'text-xs min-h-[2.1rem]'} font-medium text-foreground leading-snug line-clamp-2`}>{lang === 'ar' ? (item.name_ar || item.name) : item.name}</p>
                  <p className={`${touchMode ? 'text-lg' : 'text-sm'} font-bold text-amber-600 mt-0.5`}>{item.price} {t('common.sar')}</p>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* RIGHT: Cart (30%) */}
        <div className="w-80 lg:w-96 border-s border-border/50 flex flex-col bg-card/50 flex-shrink-0">
          {/* Order Type */}
          <div className="p-3 border-b border-border/50">
            <div className="grid grid-cols-3 gap-1.5">
              {[{ key: 'dine_in', icon: UtensilsCrossed, label: t('pos.dineIn') }, { key: 'delivery', icon: Truck, label: t('pos.delivery') }, { key: 'pickup', icon: Package, label: t('pos.pickup') }].map(ot => (
                <button key={ot.key} onClick={() => setOrderType(ot.key)} data-testid={`pos-type-${ot.key}`}
                  className={`${touchMode ? 'py-3 text-sm' : 'py-2 text-xs'} px-2 rounded-lg font-medium flex items-center justify-center gap-1 transition-all ${orderType === ot.key ? 'bg-amber-500 text-white' : 'bg-muted text-muted-foreground'}`}>
                  <ot.icon className={touchMode ? 'w-4 h-4' : 'w-3.5 h-3.5'} />{ot.label}
                </button>
              ))}
            </div>
            {orderType === 'dine_in' && (
              <Input value={tableNumber} onChange={e => setTableNumber(e.target.value)} type="number" placeholder={t('pos.tableNumber')} className="mt-2 h-8 text-sm" data-testid="pos-table-number" />
            )}
            {orderType === 'delivery' && (
              <div className="mt-2 space-y-1.5">
                <Input value={customerName} onChange={e => setCustomerName(e.target.value)} placeholder={t('pos.customerName')} className="h-8 text-sm" />
                <Input value={customerPhone} onChange={e => setCustomerPhone(e.target.value)} placeholder={t('pos.customerPhone')} className="h-8 text-sm" />
                <Input value={customerAddress} onChange={e => setCustomerAddress(e.target.value)} placeholder={t('pos.customerAddress')} className="h-8 text-sm" />
              </div>
            )}
          </div>

          {/* Cart Items */}
          <div className="flex-1 overflow-y-auto p-3 space-y-2">
            {cart.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <ShoppingCart className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p className="text-sm">{t('pos.emptyCart')}</p>
                <p className="text-xs">{t('pos.addItems')}</p>
              </div>
            ) : cart.map(item => (
              <div key={item.item_id} className={`bg-background rounded-lg ${touchMode ? 'p-3' : 'p-2.5'} flex items-center gap-2`} data-testid={`pos-cart-item-${item.item_id}`}>
                <div className="flex-1 min-w-0">
                  <p className={`${touchMode ? 'text-sm' : 'text-xs'} font-medium truncate`}>{lang === 'ar' ? (item.name_ar || item.name) : item.name}</p>
                  <p className={`${touchMode ? 'text-sm' : 'text-xs'} text-amber-600 font-medium`}>{(item.price * item.quantity).toFixed(2)} {t('common.sar')}</p>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => updateQty(item.item_id, -1)} className={`${touchMode ? 'w-9 h-9' : 'w-6 h-6'} rounded-md bg-muted flex items-center justify-center hover:bg-red-100 dark:hover:bg-red-500/20`}><Minus className={touchMode ? 'w-4 h-4' : 'w-3 h-3'} /></button>
                  <span className={`${touchMode ? 'w-8 text-base' : 'w-6 text-sm'} text-center font-medium`}>{item.quantity}</span>
                  <button onClick={() => updateQty(item.item_id, 1)} className={`${touchMode ? 'w-9 h-9' : 'w-6 h-6'} rounded-md bg-muted flex items-center justify-center hover:bg-emerald-100 dark:hover:bg-emerald-500/20`}><Plus className={touchMode ? 'w-4 h-4' : 'w-3 h-3'} /></button>
                  <button onClick={() => removeFromCart(item.item_id)} className={`${touchMode ? 'w-9 h-9' : 'w-6 h-6'} rounded-md flex items-center justify-center text-red-400 hover:bg-red-100 dark:hover:bg-red-500/20 ms-1`}><Trash2 className={touchMode ? 'w-4 h-4' : 'w-3 h-3'} /></button>
                </div>
              </div>
            ))}
          </div>

          {/* Discount */}
          {cart.length > 0 && (
            <div className="px-3 pb-2">
              <div className="flex gap-1.5">
                <button onClick={() => { if (discountType === 'percent' && discountValue === '30') { setDiscountType(''); setDiscountValue(''); } else { setDiscountType('percent'); setDiscountValue('30'); } }}
                  data-testid="pos-quick-discount-30"
                  className={`flex-1 ${touchMode ? 'py-2.5 text-sm' : 'py-1.5 text-xs'} rounded-md font-bold flex items-center justify-center gap-1 ${discountType === 'percent' && discountValue === '30' ? 'bg-emerald-600 text-white' : 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400 hover:bg-emerald-100'}`}>
                  {t('pos.discount30')}
                </button>
                <button onClick={() => setDiscountType(discountType === 'percent' ? '' : 'percent')}
                  className={`flex-1 ${touchMode ? 'py-2.5 text-sm' : 'py-1.5 text-xs'} rounded-md font-medium flex items-center justify-center gap-1 ${discountType === 'percent' ? 'bg-amber-500 text-white' : 'bg-muted text-muted-foreground'}`}>
                  <Percent className="w-3 h-3" />{t('pos.discountPercent')}
                </button>
                <button onClick={() => setDiscountType(discountType === 'fixed' ? '' : 'fixed')}
                  className={`flex-1 ${touchMode ? 'py-2.5 text-sm' : 'py-1.5 text-xs'} rounded-md font-medium flex items-center justify-center gap-1 ${discountType === 'fixed' ? 'bg-amber-500 text-white' : 'bg-muted text-muted-foreground'}`}>
                  <DollarSign className="w-3 h-3" />{t('pos.discountFixed')}
                </button>
              </div>
              {discountType && (
                <div className="flex gap-1.5 mt-1.5">
                  <Input type="number" value={discountValue} onChange={e => setDiscountValue(e.target.value)}
                    placeholder={discountType === 'percent' ? '10' : '20'} className="h-8 text-sm flex-1" data-testid="pos-discount-value" />
                  <Input value={discountReason} onChange={e => setDiscountReason(e.target.value)}
                    placeholder={t('pos.discountReason')} className="h-8 text-sm flex-1" />
                </div>
              )}
            </div>
          )}

          {/* Notes */}
          {cart.length > 0 && (
            <div className="px-3 pb-2">
              <Input value={notes} onChange={e => setNotes(e.target.value)} placeholder={t('pos.notes')} className="h-8 text-sm" />
            </div>
          )}

          {/* Totals + Checkout */}
          <div className="p-3 border-t border-border/50 space-y-1.5 bg-card">
            <div className="flex justify-between text-sm"><span className="text-muted-foreground">{t('pos.subtotal')}</span><span data-testid="pos-cart-subtotal">{subtotal.toFixed(2)} {t('common.sar')}</span></div>
            {discountAmount > 0 && <div className="flex justify-between text-sm text-red-500"><span>{t('pos.discount')}</span><span data-testid="pos-cart-discount">-{discountAmount.toFixed(2)} {t('common.sar')}</span></div>}
            {roundingAdj > 0 && <div className="flex justify-between text-sm text-red-500"><span>{t('pos.rounding')}</span><span data-testid="pos-cart-rounding">-{roundingAdj.toFixed(2)} {t('common.sar')}</span></div>}
            <div className="flex justify-between text-sm text-muted-foreground"><span>{t('pos.vatIncluded')}</span><span data-testid="pos-cart-vat">{vat.toFixed(2)} {t('common.sar')}</span></div>
            <div className="flex justify-between font-bold text-lg border-t border-border pt-1.5"><span>{t('pos.total')}</span><span className="text-amber-600" data-testid="pos-cart-total">{total.toFixed(2)} {t('common.sar')}</span></div>
            <Button onClick={() => setShowPayment(true)} disabled={cart.length === 0 || loading}
              className={`w-full ${touchMode ? 'h-14 text-lg' : 'h-12 text-base'} bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 mt-2`} data-testid="pos-checkout-btn">
              <ShoppingCart className="w-5 h-5 me-2" />{t('pos.checkout')} - {total.toFixed(2)} {t('common.sar')}
            </Button>
          </div>
        </div>
      </div>

      {/* Modals */}
      {showPayment && <PaymentModal cart={cart} subtotal={subtotal} discountAmount={discountAmount} vat={vat} total={total} onConfirm={handleConfirmPayment} onClose={() => setShowPayment(false)} t={t} loading={loading} />}
      {showReturns && <ReturnsModal branch={activeBranch} businessSettings={businessSettings} onClose={() => setShowReturns(false)} onDone={checkShift} t={t} lang={lang} API={API} authHeaders={authHeaders} />}
      {showCloseShift && <CloseShiftModal shift={shift} onConfirm={closeShift} onClose={() => setShowCloseShift(false)} t={t} />}
      {completedOrder && <ReceiptModal order={completedOrder} branch={activeBranch} businessSettings={businessSettings} onClose={() => setCompletedOrder(null)} onNewOrder={handleNewOrder} t={t} dir={dir} />}
    </div>
  );
}
